import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { AlertCircle, CheckCircle, CreditCard, Loader2, Mail, RefreshCw } from 'lucide-react';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { useCart } from '@/contexts/CartContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { clearPendingPayment, getPaymentModeLabel, readPendingPayment, storeConfirmation } from '@/pages/order/paymentFlow';

const DEFAULT_STATUS = {
  kind: 'loading',
  title: 'Checking payment status',
  message: 'We’re reviewing your card payment now.',
};

export default function PaymentStatusPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [refreshTick, setRefreshTick] = useState(0);
  // Slice 7.2: after 30s of continuous 'processing' state, stop polling and
  // surface the Support hint. `startedAt` is set the first time we enter
  // processing state; `stalled` gates the UI change.
  const finalizeRef = useRef(false);
  const processingStartRef = useRef(null);
  const [stalled, setStalled] = useState(false);
  const STALLED_MS = 30_000;

  useSeoMeta({ seo_title: 'Payment Status - The Fashion Detective' });

  useEffect(() => {
    api.get('/config').then((response) => setConfig(response.data)).catch(() => {
      setStatus({
        kind: 'error',
        title: 'Unable to load payment status',
        message: 'We could not load the payment configuration. Please try again from checkout.',
      });
    });
  }, []);

  const pendingPayment = useMemo(() => readPendingPayment(), []);
  const orderId = searchParams.get('order_id') || pendingPayment?.order_id || '';
  const clientSecret = searchParams.get('payment_intent_client_secret') || pendingPayment?.client_secret || '';
  const paymentIntentId = searchParams.get('payment_intent') || pendingPayment?.payment_intent_id || '';
  const redirectStatus = searchParams.get('redirect_status') || '';
  const paymentMode = config?.payment_mode || 'mock';

  useEffect(() => {
    if (!config) return;

    const finalizeOrder = async () => {
      if (!orderId || finalizeRef.current) return;
      finalizeRef.current = true;
      try {
        // Pass the held attempt identifiers so confirm-payment can target the exact
        // attempt even if the active-attempt pointer was cleared (CHK-08 — no dead-end).
        const confirmBody = {};
        if (paymentIntentId) confirmBody.payment_intent_id = paymentIntentId;
        const response = await api.post(`/orders/${orderId}/confirm-payment`, confirmBody);
        if (response.data?.success) {
          const confirmationSource = readPendingPayment() || pendingPayment || {};
          storeConfirmation({
            order_id: orderId,
            order_number: response.data.order_number || confirmationSource.order_number || orderId,
            total_cents: response.data.total_charged_cents || confirmationSource.total_cents || 0,
            items_count: confirmationSource.items_count || 0,
            items_summary: confirmationSource.items_summary || [],
          });
          clearPendingPayment();
          clearCart();
          navigate(`/order/confirmation/${orderId}`, { replace: true });
          return;
        }

        finalizeRef.current = false;
        setStatus({
          kind: 'error',
          title: 'Payment needs attention',
          message: response.data?.error || 'Your payment was not finalized yet. Please return to checkout and try again.',
        });
      } catch (error) {
        finalizeRef.current = false;
        setStatus({
          kind: 'error',
          title: 'Payment verification failed',
          message: error.response?.data?.detail || 'We could not confirm your payment yet. Please try again from checkout.',
        });
      }
    };

    const inspectPayment = async () => {
      if (paymentMode === 'mock') {
        setStatus({
          kind: 'missing',
          title: 'Mock payments do not return here',
          message: 'This page is only used for card payment return flows. You can go back to checkout safely.',
        });
        return;
      }

      if (!orderId || !clientSecret || !config.stripe_publishable_key) {
        setStatus({
          kind: 'missing',
          title: 'Missing payment details',
          message: 'We could not find the payment details needed to verify this order. Return to checkout to try again.',
        });
        return;
      }

      try {
        const stripe = await loadStripe(config.stripe_publishable_key);
        const result = await stripe?.retrievePaymentIntent(clientSecret);
        const paymentIntent = result?.paymentIntent;
        const error = result?.error;

        if (error) {
          setStatus({
            kind: 'error',
            title: 'Payment check failed',
            message: error.message || 'Could not verify this payment. Please retry from checkout.',
          });
          return;
        }

        if (!paymentIntent) {
          setStatus({
            kind: 'missing',
            title: 'Payment not found',
            message: 'No payment intent was returned for this request.',
          });
          return;
        }

        if (paymentIntent.status === 'succeeded') {
          setStatus({
            kind: 'success',
            title: 'Payment received',
            message: 'Your payment succeeded. We’re finalizing your order now.',
          });
          await finalizeOrder();
          return;
        }

        if (paymentIntent.status === 'processing') {
          // Track continuous processing time. If we've been polling >30s, tell the user to check email.
          if (!processingStartRef.current) processingStartRef.current = Date.now();
          const elapsed = Date.now() - processingStartRef.current;
          if (elapsed >= STALLED_MS) {
            setStalled(true);
            setStatus({
              kind: 'processing',
              title: 'Still processing — we\'ll email you when this settles',
              message:
                'Your card payment is taking longer than expected to confirm. You can safely leave this page — we\'ll email you at the address on your order the moment it lands.',
            });
            return;
          }
          setStatus({
            kind: 'processing',
            title: 'Payment is processing',
            message: 'Your card payment is still processing. Please refresh status in a moment.',
          });
          return;
        }

        if (paymentIntent.status === 'requires_action') {
          setStatus({
            kind: 'processing',
            title: 'Additional action may still be in progress',
            message: 'Your card payment is still waiting on the return step. Try refreshing this page after the redirect completes.',
          });
          return;
        }

        setStatus({
          kind: 'error',
          title: 'Payment was not completed',
          message: `Current payment status: ${paymentIntent.status || redirectStatus || 'unknown'}.`,
        });
      } catch {
        setStatus({
          kind: 'error',
          title: 'Unable to review payment',
          message: 'We hit a problem while checking your card payment. Please try again from checkout.',
        });
      }
    };

    setStatus(DEFAULT_STATUS);
    inspectPayment();
  }, [clearCart, clientSecret, config, navigate, orderId, paymentIntentId, paymentMode, pendingPayment, redirectStatus, refreshTick]);

  const statusColor = status.kind === 'success'
    ? '#0E6B5D'
    : status.kind === 'processing'
      ? '#A36A3A'
      : '#5A1F2A';

  const StatusIcon = status.kind === 'success'
    ? CheckCircle
    : status.kind === 'processing' || status.kind === 'loading'
      ? Loader2
      : AlertCircle;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14" data-testid="payment-status-page">
      <Card className="bg-white border-stone/50">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div className="flex items-center gap-3">
            <div className="rounded-full p-3" style={{ background: 'rgba(14,107,93,0.08)' }}>
              <CreditCard className="h-5 w-5" style={{ color: '#0E6B5D' }} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Payment return</p>
              <h1 className="font-display text-2xl sm:text-3xl font-medium text-ink" data-testid="payment-status-title">Order payment status</h1>
            </div>
          </div>

          {config && (
            <Badge
              className="w-fit text-[10px]"
              style={{ background: 'rgba(14,107,93,0.10)', color: '#0E6B5D' }}
              data-testid="payment-status-mode-badge"
            >
              {getPaymentModeLabel(config.payment_mode)}
            </Badge>
          )}

          <div className="rounded-2xl border border-stone/40 bg-stone-50 p-5 space-y-3" data-testid="payment-status-message-card">
            <div className="flex items-start gap-3">
              <StatusIcon className={`h-5 w-5 mt-0.5 ${status.kind === 'loading' || status.kind === 'processing' ? 'animate-spin' : ''}`} style={{ color: statusColor }} />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-ink">{status.title}</p>
                <p className="text-sm text-muted-foreground" data-testid="payment-status-message">{status.message}</p>
              </div>
            </div>
            {orderId && <p className="text-xs text-muted-foreground" data-testid="payment-status-order-id">Order reference: {orderId}</p>}
            {redirectStatus && <p className="text-xs text-muted-foreground">Redirect status: {redirectStatus}</p>}
          </div>

          <div className="flex flex-wrap gap-3">
            {(status.kind === 'processing' || status.kind === 'error') && !stalled && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setRefreshTick((value) => value + 1)}
                data-testid="payment-status-refresh-button"
              >
                <RefreshCw className="h-4 w-4" /> Refresh status
              </Button>
            )}
            {stalled && (
              <a
                href={`mailto:${config?.settings?.['contact.support_email'] || 'support@thefashiondetective.com'}?subject=Payment%20verification%20(Order%20${orderId})`}
                data-testid="payment-status-contact-support"
              >
                <Button variant="outline" className="gap-2">
                  <Mail className="h-4 w-4" /> Contact support
                </Button>
              </a>
            )}
            <Button
              variant={status.kind === 'success' ? 'outline' : 'default'}
              onClick={() => navigate('/order/checkout')}
              data-testid="payment-status-return-checkout-button"
            >
              Return to checkout
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}